import React from "react";
import { Drawer, Descriptions, Tag, Tabs, Typography, Space, Divider } from "antd";
import { formatDateTime } from "../../../../utils/dateUtils";
import AircraftTag from "../../../../components/AircraftTag";

const { TabPane } = Tabs;
const { Text } = Typography;

// 状态标签映射
const STATUS_CONFIG = {
    PENDING: { color: "orange", text: "待处理" },
    APPROVED: { color: "green", text: "已批准" },
    REJECTED: { color: "red", text: "已拒绝" },
    CANCELLED: { color: "gray", text: "已取消" },
};

/**
 * 许可申请详情抽屉组件
 * @param {Object} props
 * @param {boolean} props.visible - 是否显示抽屉
 * @param {Function} props.onClose - 关闭抽屉回调
 * @param {Object} props.permitRequest - 许可申请数据
 */
const PermitRequestDetailDrawer = ({ visible, onClose, permitRequest }) => {
    if (!permitRequest) return null;

    // 渲染状态标签
    const renderStatusTag = (status) => {
        if (!status) return <Tag color="default">无状态</Tag>;
        
        const config = STATUS_CONFIG[status] || { color: "default", text: status };
        return <Tag color={config.color}>{config.text}</Tag>;
    };

    // 渲染许可详情子部分
    const renderPermissionSection = (permission, title) => {
        if (!permission) {
            return (
                <Descriptions size="small" column={1} bordered>
                    <Descriptions.Item label="状态">
                        <Text type="secondary">未设置</Text>
                    </Descriptions.Item>
                </Descriptions>
            );
        }

        return (
            <Descriptions size="small" column={2} bordered>
                <Descriptions.Item label="状态" span={2}>
                    {renderStatusTag(permission.status)}
                </Descriptions.Item>
                
                {permission.referenceNumber && (
                    <Descriptions.Item label="参考编号">
                        {permission.referenceNumber}
                    </Descriptions.Item>
                )}
                
                {permission.manifestNumber && (
                    <Descriptions.Item label="舱单编号">
                        {permission.manifestNumber}
                    </Descriptions.Item>
                )}
                
                {permission.std && (
                    <Descriptions.Item label="计划起飞时间">
                        {formatDateTime(permission.std)}
                    </Descriptions.Item>
                )}
                
                {permission.sta && (
                    <Descriptions.Item label="计划到达时间">
                        {formatDateTime(permission.sta)}
                    </Descriptions.Item>
                )}
                
                {permission.delayHours !== undefined && permission.delayHours !== null && (
                    <Descriptions.Item label="延误小时数">
                        {permission.delayHours} 小时
                    </Descriptions.Item>
                )}
                
                {permission.remarks && (
                    <Descriptions.Item label="备注" span={2}>
                        {permission.remarks}
                    </Descriptions.Item>
                )}
            </Descriptions>
        );
    };

    // 获取航班信息
    const flight = permitRequest.flight;

    return (
        <Drawer
            title="许可申请详情"
            width={800}
            open={visible}
            onClose={onClose}
            destroyOnHidden
        >
            {/* 基本信息 */}
            <Descriptions title="基本信息" size="small" column={2} bordered style={{ marginBottom: 24 }}>
                <Descriptions.Item label="申请ID">
                    {permitRequest.id}
                </Descriptions.Item>
                
                <Descriptions.Item label="主状态">
                    {renderStatusTag(permitRequest.status)}
                </Descriptions.Item>
                
                <Descriptions.Item label="批次号">
                    {permitRequest.batch_id || <Text type="secondary">无</Text>}
                </Descriptions.Item>
                
                <Descriptions.Item label="创建时间">
                    {formatDateTime(permitRequest.created_at)}
                </Descriptions.Item>
                
                <Descriptions.Item label="更新时间">
                    {formatDateTime(permitRequest.updated_at)}
                </Descriptions.Item>
                
                <Descriptions.Item label="关联邮件ID">
                    {permitRequest.related_latest_email_id || <Text type="secondary">无</Text>}
                </Descriptions.Item>
                
                {permitRequest.remarks && (
                    <Descriptions.Item label="备注" span={2}>
                        {permitRequest.remarks}
                    </Descriptions.Item>
                )}
            </Descriptions>

            {/* 航班信息 */}
            {flight && (
                <>
                    <Descriptions title="关联航班信息" size="small" column={2} bordered style={{ marginBottom: 24 }}>
                        <Descriptions.Item label="航班号">
                            {flight.flight_number}
                        </Descriptions.Item>
                        
                        <Descriptions.Item label="航班日期">
                            {formatDateTime(flight.flight_date, 'YYYY-MM-DD')}
                        </Descriptions.Item>
                        
                        <Descriptions.Item label="航线">
                            {flight.departure_airport} → {flight.arrival_airport}
                        </Descriptions.Item>
                        
                        <Descriptions.Item label="飞机">
                            {flight.aircraft_reg ? (
                                <AircraftTag registration={flight.aircraft_reg} />
                            ) : (
                                <Text type="secondary">未分配</Text>
                            )}
                        </Descriptions.Item>
                        
                        <Descriptions.Item label="计划起飞">
                            {formatDateTime(flight.std)}
                        </Descriptions.Item>
                        
                        <Descriptions.Item label="计划到达">
                            {formatDateTime(flight.sta)}
                        </Descriptions.Item>
                    </Descriptions>
                </>
            )}

            {/* 许可详情 */}
            <div style={{ marginBottom: 24 }}>
                <Typography.Title level={5} style={{ marginBottom: 16 }}>
                    许可详情
                </Typography.Title>
                
                {permitRequest.details ? (
                    <Tabs defaultActiveKey="departure" size="small">
                        {permitRequest.details.departureLandingPermission && (
                            <TabPane tab="始发机场起降许可" key="departure">
                                {renderPermissionSection(
                                    permitRequest.details.departureLandingPermission,
                                    "始发机场起降许可"
                                )}
                            </TabPane>
                        )}
                        
                        {permitRequest.details.arrivalLandingPermission && (
                            <TabPane tab="目的机场起降许可" key="arrival">
                                {renderPermissionSection(
                                    permitRequest.details.arrivalLandingPermission,
                                    "目的机场起降许可"
                                )}
                            </TabPane>
                        )}
                        
                        {permitRequest.details.overFlightPermission && (
                            <TabPane tab="飞越许可" key="overflight">
                                {renderPermissionSection(
                                    permitRequest.details.overFlightPermission,
                                    "飞越许可"
                                )}
                            </TabPane>
                        )}
                    </Tabs>
                ) : (
                    <Text type="secondary">暂无许可详情</Text>
                )}
            </div>
        </Drawer>
    );
};

export default PermitRequestDetailDrawer; 